import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Error } from '@/components/Error/Error';
import { ParticipantEnum, ROUTES } from '@/constants/common';
import { useDashboard } from '@/hooks/useDashboard';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';

import { AdSection } from '../AdSection';
import * as S from '../Dashboard.styles';
import { NoDashboardData } from '../NoDashboardData';

type Props = { participant: ParticipantEnum };

export const AdsBlock: FC<Props> = ({ participant }) => {
  const { t } = useTranslation();
  const {
    hasMore,
    featuredAds,
    newAds,
    loading,
    newAdsLoading,
    isNoData,
    isNewCardsView,
    isFeaturedCardsView,
    isLoadingMore,
    isLoadingMoreNewAds,
    error,
    fetchMoreAds
  } = useDashboard(participant);

  const { loadMoreRef } = useInfiniteScroll(fetchMoreAds, hasMore);

  if (error) {
    return <Error />;
  }

  const participantLower = participant.toLowerCase();

  const newAdsTitle = t(`MainPage.new_${participantLower}`);
  const featuredAdsTitle = t(`MainPage.featured_${participantLower}`);

  const noDataTitle = t(`Dashboard.no_data_title`);

  const noDataDescription = t(
    participant === ParticipantEnum.RESUME
      ? 'Dashboard.no_data_resumes_description'
      : 'Dashboard.no_data_vacancies_description'
  );

  const newAdsPath =
    participant === ParticipantEnum.RESUME ? ROUTES.RESUMES : ROUTES.VACANCIES;

  return isNoData ? (
    <NoDashboardData title={noDataTitle} description={noDataDescription} />
  ) : (
    <S.ContentWrapper>
      {isNewCardsView && (
        <AdSection
          path={newAdsPath}
          titleStyles={{ paddingTop: '10px' }}
          title={newAdsTitle}
          ads={newAds}
          loading={newAdsLoading}
          isLoadingMore={isLoadingMoreNewAds}
        />
      )}
      {isFeaturedCardsView && (
        <AdSection
          titleStyles={{ paddingTop: '10px' }}
          title={featuredAdsTitle}
          ads={featuredAds}
          loading={loading}
          isLoadingMore={isLoadingMore}
          isLast
        />
      )}
      {hasMore && <div ref={loadMoreRef} />}
    </S.ContentWrapper>
  );
};
